/**
 * 청소년(만 19세 이하) 사용자를 위한 이미지 마스킹 헬퍼
 */

// 마스킹이 필요한 7개 이미지 목록
const MASKABLE_IMAGES = [
    'image_cross_polar_front',
    'image_uv_front',
    'image_red_front',
    'image_grey_front',
    'image_brown_front',
    'image_cross_polar_right',
    'image_homo_front'
];

/**
 * 생년월일로 나이 계산 (한국 나이 아닌 만 나이)
 * @param {string} birthDate - YYYY-MM-DD 형식의 생년월일
 * @returns {number} 만 나이
 */
export const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    
    const today = new Date();
    const birth = new Date(birthDate);
    
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
        age--;
    }
    
    return age;
};

/**
 * 청소년 여부 확인 (만 19세 이하)
 * @param {string} birthDate - YYYY-MM-DD 형식의 생년월일
 * @returns {boolean} 청소년 여부
 */
export const isJuniorUser = (birthDate) => {
    const age = calculateAge(birthDate);
    return age !== null && age <= 19;
};

/**
 * 이미지 URL을 마스킹된 버전으로 변환
 * @param {string} imageUrl - 원본 이미지 URL
 * @returns {string} 마스킹된 이미지 URL
 */
export const getMaskedImageUrl = (imageUrl) => {
    if (!imageUrl) return imageUrl;
    
    // 원본이 PNG든 JPG든, 마스킹된 파일은 _masked.jpg로 저장되어 있음
    // .png를 _masked.jpg로, .jpg를 _masked.jpg로 변경
    if (imageUrl.includes('.png')) {
        return imageUrl.replace(/\.png$/, '_masked.jpg');
    } else if (imageUrl.includes('.jpg')) {
        return imageUrl.replace(/\.jpg$/, '_masked.jpg');
    }
    
    return imageUrl;
};

/**
 * 이미지 리스트에서 필요한 이미지들만 마스킹 처리
 * @param {object} imgList - 이미지 URL 객체
 * @param {boolean} shouldMask - 마스킹 여부
 * @returns {object} 처리된 이미지 URL 객체
 */
export const processMaskedImages = (imgList, shouldMask) => {
    if (!shouldMask || !imgList) return imgList;
    
    const maskedList = { ...imgList };
    
    MASKABLE_IMAGES.forEach(imageKey => {
        if (maskedList[imageKey]) {
            maskedList[imageKey] = getMaskedImageUrl(maskedList[imageKey]);
        }
    });
    
    return maskedList;
};

/**
 * 아바타 모드 - 모든 이미지를 성별에 맞는 아바타로 교체
 * @param {object} imgList - 이미지 URL 객체
 * @param {string|number} gender - 사용자 성별 (1=남자, 2=여자, 'M', 'F', 'male', 'female')
 * @returns {object} 아바타로 교체된 이미지 URL 객체
 */
export const processAvatarMode = (imgList, gender) => {
    if (!imgList) return imgList;

    // 성별에 따른 아바타 이미지 경로
    // gender === 1 (남자), gender === 2 (여자) - 숫자 타입 처리
    const isMale = gender === 1 || gender === '1' ||
                   gender === 'M' || gender === 'male' || gender === '남';

    const avatarUrl = isMale ? '/avatar/male.png' : '/avatar/female.png';

    const avatarList = {};

    // 모든 이미지를 아바타로 교체
    Object.keys(imgList).forEach(key => {
        avatarList[key] = avatarUrl;
    });

    console.log('🎭 Avatar mode activated! Gender:', gender, '(1=남자, 2=여자), Avatar URL:', avatarUrl);

    return avatarList;
};

/**
 * 리포트 데이터의 이미지를 청소년 여부에 따라 처리
 * @param {object} reportData - 리포트 전체 데이터
 * @param {boolean} avatarMode - 아바타 모드 활성화 여부
 * @returns {object} 처리된 리포트 데이터
 */
export const processReportImagesForJunior = (reportData, avatarMode = false) => {
    console.log('🔍 processReportImagesForJunior 호출됨');
    console.log('전체 reportData:', reportData);
    console.log('Avatar mode:', avatarMode);

    if (!reportData) {
        console.log('❌ reportData가 없음');
        return reportData;
    }

    // 아바타 모드가 활성화된 경우
    if (avatarMode && reportData.imgList) {
        const userGender = reportData.user?.gender || reportData.gender || 2; // 기본값을 2(여자)로 설정
        console.log('🎭 Avatar mode processing - Gender:', userGender, '(1=남자, 2=여자)');

        return {
            ...reportData,
            imgList: processAvatarMode(reportData.imgList, userGender),
            isAvatarMode: true
        };
    }

    // user가 없을 수도 있으니 직접 birth를 찾아보자
    const birthDate = reportData.user?.birth || reportData.birth;
    const age = calculateAge(birthDate);
    const isJunior = isJuniorUser(birthDate);

    // 디버깅 로그
    console.log('=== 청소년 마스킹 체크 ===');
    console.log('생년월일:', birthDate);
    console.log('계산된 나이:', age);
    console.log('청소년 여부 (19세 이하):', isJunior);

    // 청소년이면 마스킹된 이미지 사용
    if (isJunior && reportData.imgList) {
        console.log('✅ 청소년 사용자 확인! 이미지 마스킹 적용됨');
        console.log('마스킹되는 이미지:', MASKABLE_IMAGES);

        return {
            ...reportData,
            imgList: processMaskedImages(reportData.imgList, true),
            isJuniorUser: true // 디버깅/표시용 플래그
        };
    }

    console.log('❌ 성인 사용자 - 마스킹 적용 안 함');
    return {
        ...reportData,
        isJuniorUser: false
    };
};